import { useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../Redux/hooks';
import { loadEnsembles, removeEnsemble } from '../../../Redux/ensemblesSlice';
import { useTranslation } from '../../../i18n/useTranslation';
import { WEEKDAY_KEYS } from '../../../i18n/weekdays';
import type { TranslationKey } from '../../../i18n/he';
import type { EnsembleModel } from '../../../Models/EnsembleModel';
import { EnsembleBilling } from '../../../Models/enums';
import { displayNameOf, ensembleDisplayName } from '../../../Utils/DisplayNameUtil';
import { ConfirmDialog } from '../../SharedArea/ConfirmDialog/ConfirmDialog';
import { EmptyState } from '../../SharedArea/EmptyState/EmptyState';
import { ErrorState } from '../../SharedArea/ErrorState/ErrorState';
import { MoneyText } from '../../SharedArea/MoneyText/MoneyText';
import { Spinner } from '../../SharedArea/Spinner/Spinner';
import { EnsembleEditor } from './EnsembleEditor';
import { ScheduleMeeting } from './ScheduleMeeting';
import './Ensembles.css';

const BILLING_LABELS: Record<string, TranslationKey> = {
  [EnsembleBilling.PerStudent]: 'billingPerStudent',
  [EnsembleBilling.Split]: 'billingSplit',
  [EnsembleBilling.SinglePayer]: 'billingSinglePayer',
  [EnsembleBilling.Institution]: 'billingInstitution'
};

/**
 * The groups a tutor teaches.
 *
 * Loaded here rather than in AuthGate: unlike the roster and the ledger, no
 * other screen needs this list, and a tutor with no groups should not pay for
 * it on every sign-in.
 *
 * Archived groups are hidden by default rather than deleted, on the same
 * reasoning as an archived student: the lessons a group produced are income
 * already earned, and disbanding it should not put them out of reach.
 */
export function Ensembles() {
  const { tr, language } = useTranslation();
  const dispatch = useAppDispatch();
  const ensembles = useAppSelector(state => state.ensembles.items);
  const state = useAppSelector(store => store.ensembles.state);
  const error = useAppSelector(store => store.ensembles.error);
  const students = useAppSelector(store => store.students.items);

  const [query, setQuery] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  // A name rather than a snapshot, exactly as the roster does it: the slot
  // sub-editor saves straight to the store, and a captured object would leave
  // the modal rendering a list the server has already changed.
  const [editingName, setEditingName] = useState<string | null | undefined>(undefined);
  const editing = editingName === undefined
    ? undefined
    : editingName === null
      ? null
      : ensembles.find(ensemble => ensemble.name === editingName) ?? null;
  const [scheduling, setScheduling] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<EnsembleModel | null>(null);

  useEffect(() => {
    if (state === 'idle') void dispatch(loadEnsembles());
  }, [state, dispatch]);

  const visible = useMemo(() => {
    const locale = language === 'he' ? 'he-IL' : 'en-US';
    const needle = query.trim().toLocaleLowerCase(locale);
    return ensembles.filter(ensemble => {
      if (!showArchived && ensemble.archived === 1) return false;
      if (!needle) return true;
      // Both spellings and the members, so a group can be found by whoever is
      // in it without the tutor first remembering what they called it.
      const haystack = [ensemble.name, ensemble.nameEn, ...ensemble.members]
        .filter(Boolean)
        .join(' ')
        .toLocaleLowerCase(locale);
      return haystack.includes(needle);
    });
  }, [ensembles, query, showArchived, language]);

  const scheduled = scheduling
    ? ensembles.find(ensemble => ensemble.name === scheduling) ?? null
    : null;

  if (state === 'loading' || state === 'idle') return <Spinner />;
  if (state === 'failed') return <ErrorState code={error} onRetry={() => void dispatch(loadEnsembles())} />;

  return (
    <section className="ensembles">
      <header className="students-header">
        <h1 className="screen-title">{tr('navEnsembles')}</h1>
        <button
          type="button"
          className="primary-button students-add-btn"
          onClick={() => setEditingName(null)}
          aria-label={tr('addEnsemble')}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" fill="none" /></svg>
          <span>{tr('addEnsemble')}</span>
        </button>
      </header>

      <div className="ensembles-filters">
        <input
          type="search"
          className="text-input"
          value={query}
          onChange={event => setQuery(event.target.value)}
          placeholder={tr('search')}
          aria-label={tr('search')}
        />
        <label className="check-label">
          <input type="checkbox" checked={showArchived} onChange={e => setShowArchived(e.target.checked)} />
          <span>{tr('statusFilterArchived')}</span>
        </label>
      </div>

      {visible.length === 0
        ? query.trim()
          ? <EmptyState title={tr('noEnsemblesMatch')} hint={tr('noEnsemblesHint')} />
          : <EmptyState title={tr('noEnsembles')} hint={tr('noEnsemblesHint')} />
        : (
          <ul className="ensemble-cards">
            {visible.map(ensemble => (
              <EnsembleCard
                key={ensemble.name}
                ensemble={ensemble}
                memberName={name => displayNameOf(name, students, language)}
                onEdit={() => setEditingName(ensemble.name)}
                onSchedule={() => setScheduling(ensemble.name)}
                onDelete={() => setConfirming(ensemble)}
              />
            ))}
          </ul>
        )}

      {editing !== undefined && (
        <EnsembleEditor ensemble={editing} onClose={() => setEditingName(undefined)} />
      )}

      {scheduled && <ScheduleMeeting ensemble={scheduled} onClose={() => setScheduling(null)} />}

      {confirming && (
        <ConfirmDialog
          title={tr('deleteEnsembleTitle', ensembleDisplayName(confirming, language))}
          body={tr('deleteEnsembleBody')}
          confirmLabel={tr('delete')}
          destructive
          onCancel={() => setConfirming(null)}
          onConfirm={() => {
            void dispatch(removeEnsemble(confirming.name));
            setConfirming(null);
          }}
        />
      )}
    </section>
  );
}

interface EnsembleCardProps {
  ensemble: EnsembleModel;
  memberName: (name: string) => string;
  onEdit: () => void;
  onSchedule: () => void;
  onDelete: () => void;
}

/**
 * One group's card: who is in it, when it meets, and who pays. Those are the
 * three things a tutor looks a group up for.
 */
function EnsembleCard({ ensemble, memberName, onEdit, onSchedule, onDelete }: EnsembleCardProps) {
  const { tr, language } = useTranslation();
  const displayName = ensembleDisplayName(ensemble, language);
  const billingLabel = tr(BILLING_LABELS[ensemble.billingMode] as TranslationKey);

  return (
    <li className={`ensemble-card${ensemble.archived ? ' is-archived' : ''}`}>
      <header className="ensemble-card-header">
        <div className="ensemble-card-identity">
          <h2>{displayName}</h2>
          <p>
            <bdi>{tr('ensembleMemberCount', ensemble.members.length)}</bdi>
            {' · '}
            <bdi>{ensemble.durationMinutes}′</bdi>
          </p>
        </div>
        {ensemble.archived === 1 && <span className="student-archived-badge">{tr('archived')}</span>}
      </header>

      <div className="ensemble-billing">
        <span className="ensemble-billing-mode">{billingLabel}</span>
        {ensemble.price && <strong><MoneyText amount={ensemble.price} /></strong>}
        {ensemble.payer && <span className="ensemble-payer"><bdi>{memberName(ensemble.payer)}</bdi></span>}
      </div>

      <div className="ensemble-slot-list">
        {ensemble.slots.length > 0
          ? ensemble.slots.map(slot => (
            <span key={slot.id} className="student-slot-chip">
              <bdi>{tr(WEEKDAY_KEYS[slot.weekday])} {slot.time}</bdi>
            </span>
          ))
          : <span className="student-slot-chip is-empty">—</span>}
      </div>

      {ensemble.members.length > 0
        ? (
          <ul className="ensemble-member-chips">
            {ensemble.members.map(member => (
              <li key={member}><bdi>{memberName(member)}</bdi></li>
            ))}
          </ul>
        )
        : <p className="dashboard-quiet">{tr('ensembleNoMembers')}</p>}

      {ensemble.notes && <p className="student-notes"><span aria-hidden="true">✦</span>{language === 'en' ? ensemble.notesEn?.trim() || ensemble.notes : ensemble.notes}</p>}

      <footer className="student-card-actions">
        <button type="button" className="student-card-primary" onClick={onSchedule}>
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5v14M5 12h14" /></svg>
          <span>{tr('scheduleMeeting')}</span>
        </button>
        <button type="button" className="student-icon-button" onClick={onEdit} title={tr('edit')} aria-label={tr('edit')}>
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
        </button>
        <button type="button" className="student-icon-button is-danger" onClick={onDelete} title={tr('delete')} aria-label={tr('delete')}>
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 7h16M9 7V4h6v3m-8 0 1 13a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-13" /></svg>
        </button>
      </footer>
    </li>
  );
}
